import React, { useState, useRef, useEffect } from 'react';
import { Send, Bot, User } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import ApiClient from '../utils/api';

const ChatInterface = ({ fullscreen = false, onLogout, onAdminPanel }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => `session_${Date.now()}`);
  const [logoUrl, setLogoUrl] = useState(null);
  const messagesEndRef = useRef(null);
  const readerRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    fetchLogo();
    // Add welcome message
    const welcomeMessage = {
      id: Date.now(),
      text: 'Hello! Welcome to Apna Assistant. I\'m here to help you. How can I assist you today?',
      sender: 'bot',
      timestamp: new Date().toLocaleTimeString(),
    };
    setMessages([welcomeMessage]);
    
    return () => {
      if (readerRef.current) {
        readerRef.current.cancel();
      }
    };
  }, []);

  const fetchLogo = async () => {
    try {
      const response = await ApiClient.getCurrentLogo();
      if (response && response.logo_url) {
        setLogoUrl(response.logo_url);
      } else {
        setLogoUrl('/default.png');
      }
    } catch (error) {
      console.error('Error fetching logo:', error);
      setLogoUrl('/default.png');
    }
  };

  const botMessageIdRef = useRef(null);

  const handleStream = async (reader) => {
    const decoder = new TextDecoder();
    let done = false;
    let firstChunkReceived = false;

    while (!done) {
      try {
        const { value, done: readerDone } = await reader.read();
        done = readerDone;
        const chunk = decoder.decode(value, { stream: !done });

        if (chunk) {
          if (!firstChunkReceived) {
            firstChunkReceived = true;
            setIsLoading(false);

            botMessageIdRef.current = Date.now() + 1;
            const botMessage = {
              id: botMessageIdRef.current,
              text: chunk,
              sender: 'bot',
              timestamp: new Date().toLocaleTimeString(),
            };
            setMessages(prev => [...prev, botMessage]);
          } else {
            setMessages(prev => prev.map(msg =>
              msg.id === botMessageIdRef.current
                ? { ...msg, text: msg.text + chunk }
                : msg
            ));
          }
        }
      } catch (error) {
        console.error("Error reading from stream:", error);
        done = true; // Exit loop on error
      }
    }

    if (!firstChunkReceived) {
      setIsLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      text: inputMessage,
      sender: 'user',
      timestamp: new Date().toLocaleTimeString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await ApiClient.sendChatMessage(inputMessage, sessionId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (!response.body) {
        throw new Error('Response body is not available for streaming');
      }

      const reader = response.body.getReader();
      readerRef.current = reader;
      
      await handleStream(reader);
      
      readerRef.current = null;

    } catch (error) {
      const errorMessage = {
        id: Date.now() + 1,
        text: 'Sorry, I encountered an error. Please try again.',
        sender: 'bot',
        timestamp: new Date().toLocaleTimeString(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
<div
  style={{
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    background: 'linear-gradient(135deg, rgb(76, 56, 80) 0%, rgb(131, 187, 225) 100%)',
    overflow: 'hidden',
  }}
>
      <div style={{ padding: '20px', background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <img 
            src={logoUrl || '/default.png'} 
            alt="Logo" 
            style={{ 
              height: '40px', 
              width: 'auto', 
              borderRadius: '8px',
              objectFit: 'contain'
            }}
          />
          <h1 style={{ margin: 0, color: 'white', fontSize: '28px', fontWeight: '600' }}>Apna Assistant</h1>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          {/* <button onClick={onAdminPanel} style={{ padding: '12px 24px', border: 'none', borderRadius: '25px', background: 'rgba(255,255,255,0.2)', color: 'Black', cursor: 'pointer', backdropFilter: 'blur(10px)',fontWeight: 'Bold',transition: 'all 0.3s ease' }}>Admin</button> */}
          <button onClick={onLogout} style={{ padding: '12px 24px', border: 'none', borderRadius: '25px', background: 'rgba(255,255,255,0.2)', color: 'Black', cursor: 'pointer', backdropFilter: 'blur(10px)',fontWeight: 'Bold', transition: 'all 0.3s ease' }}>Logout</button>
        </div>
      </div>
      
      <div style={{ height: 'calc(100vh - 80px)', display: 'flex', flexDirection: 'column', background: 'rgba(255,255,255,0.95)', margin: '20px', borderRadius: '20px', boxShadow: '0 20px 40px rgba(0,0,0,0.1)', backdropFilter: 'blur(10px)', overflow: 'hidden' }}>
        {/* Messages Area */}
        <div style={{ 
          flex: 1, 
          overflowY: 'auto', 
          padding: '20px',
          borderBottom: '1px solid rgba(0,0,0,0.1)',
          background: 'transparent'
        }}>
          
          
          {messages.map((message) => (
            <div
              key={message.id}
              style={{
                display: 'flex',
                justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start',
                marginBottom: '16px'
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px',
                maxWidth: '70%',
                flexDirection: message.sender === 'user' ? 'row-reverse' : 'row'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  background: message.sender === 'user' ? '#83bbe1' : '#69667f',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  flexShrink: 0
                }}>
                  {message.sender === 'user' ? <User size={16} /> : <Bot size={16} />}
                </div>
                
                <div style={{
                  background: message.isError ? '#f8d7da' : (message.sender === 'user' 
                    ? 'linear-gradient(135deg, rgba(131, 187, 225, 1) 100%)'
                    : '#69667f'),
                  color: message.sender === 'user' ? 'black' : 'white',
                  padding: '12px 16px',
                  borderRadius: '18px',
                  border: message.isError ? '1px solid #dc3545' : 'none',
                  boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                  backdropFilter: message.sender !== 'user' ? 'blur(10px)' : 'none'
                }}>
                  <div style={{ 
                    whiteSpace: 'pre-wrap',
                    overflow: 'hidden',
                    wordWrap: 'break-word'
                  }}>
                    <ReactMarkdown 
                      components={{
                        ul: ({children}) => <ul style={{margin: '0', paddingLeft: '20px', listStylePosition: 'inside'}}>{children}</ul>,
                        ol: ({children}) => <ol style={{margin: '0', paddingLeft: '20px', listStylePosition: 'inside'}}>{children}</ol>,
                        li: ({children}) => <li style={{margin: '4px 0'}}>{children}</li>,
                        p: ({children}) => <p style={{margin: '8px 0'}}>{children}</p>
                      }}
                    >
                      {message.text}
                    </ReactMarkdown>
                  </div>
                  <div style={{
                    fontSize: '08px',
                    opacity: 0.7,
                    marginTop: '4px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <span>{message.timestamp}</span>
                    {message.responseTime && (
                      <span>{message.responseTime}ms</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
          
          {isLoading && (
            <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  background: '#6c757d',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white'
                }}>
                  <Bot size={16} />
                </div>
                <div style={{
                  background: '#f8f9fa',
                  padding: '12px 16px',
                  borderRadius: '18px',
                  color: '#666'
                }}>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <div style={{ 
                      width: '8px', 
                      height: '8px', 
                      borderRadius: '50%', 
                      background: '#ccc',
                      animation: 'pulse 1.5s ease-in-out infinite'
                    }}></div>
                    <div style={{ 
                      width: '8px', 
                      height: '8px', 
                      borderRadius: '50%', 
                      background: '#ccc',
                      animation: 'pulse 1.5s ease-in-out infinite 0.2s'
                    }}></div>
                    <div style={{ 
                      width: '8px', 
                      height: '8px', 
                      borderRadius: '50%', 
                      background: '#ccc',
                      animation: 'pulse 1.5s ease-in-out infinite 0.4s'
                    }}></div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div style={{ padding: '20px', background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)' }}>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
            <textarea
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message..."
              style={{
                flex: 1,
                padding: '12px',
                border: fullscreen ? 'none' : '1px solid #ddd',
                borderRadius: '20px',
                resize: 'none',
                minHeight: '44px',
                maxHeight: '120px',
                fontFamily: 'inherit',
                fontSize: '14px',
                background: fullscreen ? 'rgba(255,255,255,0.9)' : 'white',
                backdropFilter: fullscreen ? 'blur(10px)' : 'none',
                
                boxShadow: fullscreen ? '0 4px 15px rgba(0,0,0,0.1)' : 'none'
              }}
              rows={1}
              disabled={isLoading}
            />
            <button
              onClick={sendMessage}
              disabled={!inputMessage.trim() || isLoading}
              style={{
                padding: '12px',
                background: inputMessage.trim() && !isLoading 
                  ? (fullscreen ? 'linear-gradient(135deg, rgba(84, 153, 180, 1) 0%, rgb(131, 187, 225) 100%)' : '#007bff')
                  : '#ccc',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '44px',
                height: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: inputMessage.trim() && !isLoading ? 'pointer' : 'not-allowed',
                boxShadow: fullscreen && inputMessage.trim() && !isLoading ? '0 4px 15px rgba(102, 126, 234, 0.4)' : 'none',
                transition: 'all 0.3s ease'
              }}
            >
              <Send size={18} />
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 80%, 100% {
            opacity: 0.3;
          }
          40% {
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default ChatInterface;